import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Eye, Mail, Key, UserCheck, Send } from 'lucide-react';
import { EmailTemplateViewer } from './EmailTemplateViewer';

interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  type: 'signup' | 'password-reset' | 'confirmation' | 'invitation';
  functionName: string;
  icon: React.ComponentType<{ className?: string }>;
  status: 'active' | 'inactive';
}

const emailTemplates: EmailTemplate[] = [
  {
    id: 'confirmation',
    name: 'Email Confirmation',
    description: 'Sent to users to confirm their email address after registration',
    type: 'confirmation',
    functionName: 'send-confirmation-email',
    icon: UserCheck,
    status: 'active'
  },
  {
    id: 'password-reset',
    name: 'Password Reset',
    description: 'Sent when users request to reset their password',
    type: 'password-reset',
    functionName: 'send-password-reset',
    icon: Key,
    status: 'active'
  },
  {
    id: 'invitation',
    name: 'User Invitation',
    description: 'Sent to invite new users to join the platform',
    type: 'invitation',
    functionName: 'invite-user',
    icon: Send,
    status: 'active'
  }
];

export const EmailTemplateList = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
    ) : (
      <Badge variant="secondary">Inactive</Badge>
    );
  };

  return (
    <div className="space-y-4">
      {emailTemplates.map((template) => {
        const IconComponent = template.icon;
        
        return (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <IconComponent className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  <CardDescription>{template.description}</CardDescription>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusBadge(template.status)}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Template
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        {template.name} Template
                      </DialogTitle>
                      <DialogDescription>
                        Preview of the {template.name.toLowerCase()} email template
                      </DialogDescription>
                    </DialogHeader>
                    {selectedTemplate && (
                      <EmailTemplateViewer template={selectedTemplate} />
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center text-sm text-muted-foreground">
                <span className="font-medium">Function:</span>
                <code className="ml-2 px-2 py-1 bg-gray-100 rounded text-xs">
                  {template.functionName}
                </code>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};